export const BOOLEAN_AMENITY_KEYS = [
  "isPrivateRoom",
  "hasPrivateBathroom",
  "hasHospitalBed",
  "hasHoyerLift",
  "hasOxygen",
  "accommodatesBariatric",
];

export const ADVANCED_AMENITY_OPTIONS = [
  ["isPrivateRoom", "Private Room"],
  ["hasPrivateBathroom", "Private Bathroom"],
  ["hasHospitalBed", "Hospital Bed"],
  ["hasHoyerLift", "Hoyer Lift"],
  ["hasOxygen", "Oxygen Support"],
  ["accommodatesBariatric", "Bariatric Friendly"],
];

export const COUNTRY_ID = 228;
export const PENDING_KEY = "pendingClaim";
export const DASHBOARD_URL = "/biz/dashboard";
export const DEFAULT_RADIUS = 15;
export const INITIAL_LIMIT = 4;
export const PAGE_LIMIT = 8;
export const STATES_TTL = 24 * 60 * 60 * 1000;
export const CITIES_TTL = 6 * 60 * 60 * 1000;

export const createDefaultAdvancedFeatures = () => ({
  hasPrivateBathroom: false,
  isPrivateRoom: false,
  hasHospitalBed: false,
  hasHoyerLift: false,
  hasOxygen: false,
  accommodatesBariatric: false,
  gender: "Any",
});

export const US_STATE_CODES = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC",
]);

export const normalizeLicense = (s) =>
  (s ?? "").toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

export const isZip = (s) => /^\d{5}(-\d{4})?$/.test(String(s).trim());

export const formatMiles = (n) => (Number(n || 0) ? `${Number(n)} mi` : "");

export const shallowEq = (a, b) => {
  const ak = Object.keys(a || {});
  const bk = Object.keys(b || {});
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if ((a || {})[k] !== (b || {})[k]) return false;
  }
  return true;
};

export const idOf = (x) => String(x?.id ?? "");

export const buildAmenitiesInput = (features) => {
  const out = {};
  if (features.hasPrivateBathroom) out.hasPrivateBathroom = true;
  if (features.isPrivateRoom) out.isPrivateRoom = true;
  if (features.hasHospitalBed) out.hasHospitalBed = true;
  if (features.hasHoyerLift) out.hasHoyerLift = true;
  if (features.hasOxygen) out.hasOxygen = true;
  if (features.accommodatesBariatric) out.accommodatesBariatric = true;
  const g = (features.gender || "Any").trim();
  if (g && g !== "Any") out.gender = g;
  return Object.keys(out).length ? out : null;
};

export const encodeAmenities = (f) =>
  BOOLEAN_AMENITY_KEYS
    .filter((key) => Boolean(f?.[key]))
    .join(",");

export const decodeAmenities = (s) => {
  const out = {
    isPrivateRoom: false,
    hasPrivateBathroom: false,
    hasHospitalBed: false,
    hasHoyerLift: false,
    hasOxygen: false,
    accommodatesBariatric: false,
  };
  (s || "").split(",").forEach((k) => {
    if (k && k in out) out[k] = true;
  });
  return out;
};

export const isClaimed = (b, locallyClaimed) => {
  const raw = (b?.claimStatus ?? "").toString().trim().toUpperCase();
  const hasClaimer = b?.createdBy !== null && b?.createdBy !== undefined;
  return (
    locallyClaimed.has(idOf(b)) ||
    hasClaimer ||
    ["CLAIMED", "VERIFIED", "APPROVED", "OWNED"].includes(raw)
  );
};

export const getMatchScore = (item, query) => {
  const q = (query || "").toString().trim().toLowerCase();
  if (!q) return 0;
  const isStateCodeQuery = US_STATE_CODES.has(q.toUpperCase());

  const name = (item?.name || "").toString().toLowerCase();
  const city = (item?.cityName || "").toString().toLowerCase();
  const state = (item?.stateCode || "").toString().toLowerCase();
  const zip = (item?.zipcode || "").toString().toLowerCase();
  const text = `${name} ${city} ${state} ${zip}`;

  if (isStateCodeQuery) {
    return state === q ? 1000 : 0;
  }

  if (q.length === 1) {
    return name.startsWith(q) ? 1000 : 0;
  }

  const allowLooseContains = q.length >= 3;
  let score = 0;

  if (name === q) score += 120;
  else if (name.startsWith(q)) score += 95;
  else if (allowLooseContains && name.includes(q)) score += 70;

  if (city === q) score += 85;
  else if (city.startsWith(q)) score += 70;
  else if (allowLooseContains && city.includes(q)) score += 50;

  if (state === q) score += 80;
  if (zip === q) score += 75;
  else if (zip.startsWith(q)) score += 45;

  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    const covered = tokens.filter((t) => text.includes(t)).length;
    score += covered * 12;
  }

  if (allowLooseContains && text.includes(q)) score += 20;
  return score;
};

export const sortByMatchThenClaimed = (arr, locallyClaimed, query) =>
  arr
    .map((item, idx) => ({
      item,
      idx,
      score: getMatchScore(item, query),
      claimedRank: isClaimed(item, locallyClaimed) ? 0 : 1,
    }))
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      if (a.claimedRank !== b.claimedRank) return a.claimedRank - b.claimedRank;
      return a.idx - b.idx;
    })
    .map((x) => x.item);

export const mergeDedupeAndSort = (prev, incoming, locallyClaimed, query) => {
  if (!incoming?.length) return prev;
  const prevIds = new Set(prev.map(idOf));
  const fresh = incoming.filter((x) => !prevIds.has(idOf(x)));
  return sortByMatchThenClaimed(prev.concat(fresh), locallyClaimed, query);
};

export const buildHomeSearchSuggestions = ({
  accItems,
  citiesList,
  normalizedQuery,
  selectedStateCode,
  statesList,
}) => {
  if (!normalizedQuery) return [];

  const byPrefix = (value) => value.startsWith(normalizedQuery);

  const businesses = (accItems || [])
    .filter((business) => {
      const name = (business?.name || "").toLowerCase();
      return byPrefix(name) || name.includes(normalizedQuery);
    })
    .slice(0, 7)
    .map((business) => ({
      key: `biz-${idOf(business)}`,
      type: "business",
      label: `${business?.stateCode || "--"} | ${business?.name || ""}`,
      value: business?.name || "",
      stateCode: business?.stateCode || null,
    }));

  const cities = (citiesList || [])
    .filter((city) => {
      const cityName = (city?.cityName || "").toLowerCase();
      return byPrefix(cityName) || cityName.includes(normalizedQuery);
    })
    .slice(0, 3)
    .map((city) => ({
      key: `city-${city?.cityId ?? city?.cityName}`,
      type: "city",
      label: `${selectedStateCode || "--"} | ${city?.cityName || ""}`,
      value: city?.cityName || "",
      cityId: city?.cityId ?? null,
    }));

  const states = (statesList || [])
    .filter((state) => {
      const code = (state?.stateCode || "").toLowerCase();
      const name = (state?.stateName || "").toLowerCase();
      return code === normalizedQuery || byPrefix(name);
    })
    .slice(0, 3)
    .map((state) => ({
      key: `state-${state?.stateCode}`,
      type: "state",
      label: `${state?.stateCode || "--"} | ${state?.stateName || ""}`,
      value: state?.stateCode || "",
      stateCode: state?.stateCode || null,
    }));

  return [...businesses, ...cities, ...states].slice(0, 10);
};
